import { Component, OnInit, inject } from '@angular/core'
import { Title } from '@angular/platform-browser'
import { catchError, of } from 'rxjs'
import { IAviso } from 'src/app/core/modules/sistema/models/IAviso.interface'
import { IMenu } from 'src/app/core/modules/sistema/models/IMenu.interface'
import { AvisoService } from 'src/app/core/modules/sistema/services/aviso.service'
import { SeguridadService } from 'src/app/core/modules/sistema/services/seguridad/seguridad.service'

interface DashboardAcceso {
  etiqueta: string
  icono: string
  link: string
}

interface DashboardAccesoAutorizado extends DashboardAcceso {
  modulo: string
}

interface DashboardDefinicion {
  titulo: string
  etiqueta: string
  descripcion: string
  icono: string
  tono: 'transporte' | 'dian' | 'inventario' | 'incidentes'
  dashboardLink: string
  accesos: DashboardAcceso[]
}

interface DashboardDisponible extends DashboardDefinicion {
  accesos: DashboardAcceso[]
}

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  standalone: false,
})
export class DashboardComponent implements OnInit {
  private readonly _titleService = inject(Title)
  private readonly _seguridadService = inject(SeguridadService)
  private readonly _avisoService = inject(AvisoService)

  public nombreCuenta: string = ''
  public avisos: IAviso[] = []
  public dashboardsDisponibles: DashboardDisponible[] = []
  public accesosAutorizados: DashboardAccesoAutorizado[] = []
  public cargandoAvisos: boolean = true
  public errorAvisos: boolean = false

  private readonly dashboardsConfigurados: DashboardDefinicion[] = [
    {
      titulo: 'DIAN',
      etiqueta: 'Documentos electrónicos',
      descripcion: 'Estados de documentos, pendientes, errores, calidad de envío y comportamiento por tipo.',
      icono: 'bi bi-bank',
      tono: 'dian',
      dashboardLink: '/dian/dashboard',
      accesos: [
        { etiqueta: 'Documentos', icono: 'bi bi-file-text-fill', link: '/dian/documentos-electronicos' },
        { etiqueta: 'Títulos valores', icono: 'bi bi-file-earmark-check-fill', link: '/dian/titulos-valores' },
        { etiqueta: 'Resoluciones', icono: 'bi bi-card-checklist', link: '/dian/resoluciones' },
      ],
    },
  ]

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[])

  constructor() {}

  ngOnInit(): void {
    this._titleService.setTitle('Dashboard - A2 Hacienda')
    this.nombreCuenta = this._seguridadService.obtenerNombreCuenta()
    this.inicializarOpcionesAutorizadas()
    this.cargarAvisos()
  }

  public obtenerClaseAviso(aviso: IAviso): string {
    return `dashboard-notice-${aviso.clases || 'informacion'}`
  }

  private inicializarOpcionesAutorizadas(): void {
    const linksAutorizados = this.obtenerLinksAutorizados(this._seguridadService.Menus)

    this.dashboardsDisponibles = this.dashboardsConfigurados
      .filter((dashboard) => linksAutorizados.has(this.normalizarLink(dashboard.dashboardLink)))
      .map((dashboard) => ({
        ...dashboard,
        accesos: dashboard.accesos.filter((acceso) => linksAutorizados.has(this.normalizarLink(acceso.link))),
      }))

    this.accesosAutorizados = this.dashboardsConfigurados.flatMap((dashboard) =>
      dashboard.accesos
        .filter((acceso) => linksAutorizados.has(this.normalizarLink(acceso.link)))
        .map((acceso) => ({
          ...acceso,
          modulo: dashboard.titulo,
        })),
    )
  }

  private cargarAvisos(): void {
    this.cargandoAvisos = true
    this.errorAvisos = false

    this._avisoService
      .buscar(true)
      .pipe(
        catchError(() => {
          this.errorAvisos = true
          return of({ result: [] as IAviso[] })
        }),
      )
      .subscribe((response) => {
        this.avisos = response.result ?? []
        this.cargandoAvisos = false
      })
  }

  private obtenerLinksAutorizados(menus: IMenu[]): Set<string> {
    const links = new Set<string>()

    const recorrer = (items: IMenu[]): void => {
      items.forEach((item) => {
        if (item.link) {
          links.add(this.normalizarLink(item.link))
        }

        if (item.subMenus?.length) {
          recorrer(item.subMenus)
        }
      })
    }

    recorrer(menus)
    return links
  }

  private normalizarLink(link: string): string {
    return link.trim().toLowerCase().replace(/\/$/, '')
  }
}
